import { SceneContainer } from "GameObjects/StoryGame/SceneContainer";
import { SceneDraggable } from "GameObjects/StoryGame/SceneDraggable";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Elastic, Expo, TimelineLite, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { changeParent } from "Utils/layoutUtils";
import { list, Mix } from "Utils/Reflection";
import * as Topics from "Data/Topics";

import {ch1_p011} from "../../../layouts/ch1_p11/ch1_p011";
import { Emoji } from "GameObjects/StoryGame/ch1/Emoji";

const bubbleEmojiSpacingX = 200;
const bubbleTravelDistanceY = 200;

export class Chapter1Page11 extends AbstractStoryState {

	readonly viewport = this.addChild(new PIXI.Container());

	readonly background = this.viewport.addChild(new SceneSprite());
	readonly bubble = this.viewport.addChild(new SceneSprite());
	readonly holly = this.viewport.addChild(new SceneSprite());
	readonly mom = this.viewport.addChild(new SceneSprite());
	readonly emojiHeart = this.viewport.addChild(new Emoji());
	readonly emojiHappy = this.viewport.addChild(new Emoji());
	readonly emojiKiss = this.viewport.addChild(new Emoji());
	readonly emojiParty = this.viewport.addChild(new Emoji());
	readonly emojiFrown = this.viewport.addChild(new Emoji());
	readonly emojiSad = this.viewport.addChild(new Emoji());
	readonly emojiSurprise = this.viewport.addChild(new Emoji());
	readonly emojiHeat = this.viewport.addChild(new Emoji());
	readonly emojiThinking = this.viewport.addChild(new Emoji());
	readonly emojiWatch = this.viewport.addChild(new Emoji());

	readonly frame = this.addChild(new SceneSprite());

	readonly emoji = [
		this.emojiHeart,
		this.emojiHappy,
		this.emojiKiss,
		this.emojiParty,
		this.emojiFrown,
		this.emojiSad,
		this.emojiSurprise,
		this.emojiHeat,
		this.emojiThinking,
		this.emojiWatch,
	];

	readonly emojiChoices1 = [ this.emojiHappy, this.emojiThinking, this.emojiSad ];
	readonly emojiReply1 = [ this.emojiHeat ];
	readonly emojiChoices2 = [ this.emojiSurprise, this.emojiKiss, this.emojiParty ];
	readonly emojiReply2 = [this.emojiFrown, this.emojiWatch, this.emojiHeart];

	readonly previousBubbles = this.viewport.addChild(new PIXI.Container());

    public init(pSize: PIXI.Point, pData: any) {
        // exported layout from Animate CC
        ch1_p011.call(this);

		// super.init calls onResize
		super.init(pSize, pData);
	}

	showEmojiChoices(pEmoji: Emoji[], pCallback: (pSelected: Emoji)=>void) {
		const bubble = this.makeNewBubble();

		// TODO: simplify layout code
		let x = bubble.width - 150;
		for (let i = pEmoji.length-1; i>=0; --i) { // loop in reverse order
			const emoji = pEmoji[i];
			changeParent(emoji, bubble);
			emoji.position.y = 0;
			emoji.position.x = x * Math.sign(bubble.scale.x); // bubble might be flipped horizontally
			x -= bubbleEmojiSpacingX;
			emoji.FadeIn();
			this.showHint(emoji, "pulse");
			emoji.interactive = true;
			emoji.accessible = true;
			emoji.buttonMode = true;
			emoji.addListener(HLF.Input.Events.POINTER_TAP, ()=>{
				// prevent duplicate clicks
				emoji.interactive = false;
				emoji.accessible = false;
				emoji.buttonMode = false;
				PubSub.publishSync(Topics.HIDE_ALL_HINTS, undefined);
				pEmoji.filter(other=>other!==emoji).forEach(other=>other.FadeOut()); // fade out other emoji in bubble
				pCallback(emoji);
			});
		};

		new TimelineLite()
			// slide all bubbles up
			.to(this.previousBubbles, 0.5, {pixi: {
				y: this.previousBubbles.position.y - bubbleTravelDistanceY,
				}, ease: Expo.easeOut,
			}, 0)
			// slide bubble up and fade in
			.fromTo(bubble, 0.5, {pixi: {
				y: bubble.position.y + bubbleTravelDistanceY,
				alpha: 0,
			}, ease: Expo.easeOut,
		}, {pixi: {
			y: bubble.position.y,
			alpha: 1,
		}},0)
	}

	showEmojiResponse(pEmoji: SceneSprite[], pCallback: ()=>void) {

		const bubble = this.makeNewBubble();

		// TODO: simplify layout code
		let x = bubble.width - 250;
		for (let i = pEmoji.length-1; i>=0; --i) { // loop in reverse order
			const emoji = pEmoji[i];
			emoji.FadeIn();
			changeParent(emoji, bubble);
			emoji.position.y = 0;
			emoji.position.x = x * Math.sign(emoji.parent.scale.x); // bubble might be flipped horizontally
			x -= bubbleEmojiSpacingX;
		};

		new TimelineLite()
			// slide all bubbles up
			.to(this.previousBubbles, 0.5, {pixi: {
				y: this.previousBubbles.position.y - bubbleTravelDistanceY,
				}, ease: Expo.easeOut,
			}, 0)
			// slide bubble up and fade in
			.fromTo(bubble, 0.5, {pixi: {
				y: bubble.position.y + bubbleTravelDistanceY,
				alpha: 0,
				}, ease: Expo.easeOut,
			}, {pixi: {
				y: bubble.position.y,
				alpha: 1,
			}}, 0)
			.call(()=>{
				changeParent(bubble, this.previousBubbles);
			})
			.call(pCallback, undefined, undefined, 1.5); // callback 1s after animation complete
	}

	animateBubbleWithChosenEmoji(pEmoji: Emoji, pCallback: ()=>void) {

		const bubble = pEmoji.parent;
		changeParent(bubble, this.previousBubbles);

		new TimelineLite()
			// slide emoji to center of bubble
			.to(pEmoji, 1, {pixi: {
					x: 30 + (-pEmoji.width / 2),
				},
				ease: Expo.easeOut,
			}, 0)
			.call(pCallback, undefined, undefined, 1.5); // callback 0.5s later
	}

	public animateIn(pOnComplete: ()=>void) {
		super.animateIn(pOnComplete);

		// TODO: pubsub instead of callbacks?
		// holly speaks first
		this.bubble.scale.x *= -1;
		this.showEmojiChoices(this.emojiChoices1, (pSelected1: Emoji)=>{
			// emoji clicked
			this.hideHint();
			Application.instance.audio.play("HH_CH1_HollyTextsMomSFX_2");
			this.animateBubbleWithChosenEmoji(pSelected1, () => {
				// mom replies
				Application.instance.audio.play("HH_CH1_HollyTextsMomSFX_3");
				this.bubble.scale.x *= -1;
				this.showEmojiResponse(this.emojiReply1, ()=>{
					// holly reponds to mom
					this.bubble.scale.x *= -1;
					this.showEmojiChoices(this.emojiChoices2, (pSelected2: Emoji)=>{
						// emoji chosen
						Application.instance.audio.play("HH_CH1_HollyTextsMomSFX_4");
						this.animateBubbleWithChosenEmoji(pSelected2, ()=>{;
							// mom replies again
							Application.instance.audio.play("HH_CH1_HollyTextsMomSFX_5");
							this.bubble.scale.x *= -1;
							this.showEmojiResponse(this.emojiReply2, ()=>{
								// go to next state
								this.gotoNextState({transition: "left"});
							});
						});
					});
				});
			});
		});
		// this.showHint({x: 832, y: 284}, "hand", 5);
	}

	private makeNewBubble() {
		this.bubble.visible = false;
		const bubble = this.viewport.addChild(new SceneSprite(this.bubble.texture, this.bubble.anchor));
		bubble.position.copyFrom(this.bubble.position);
		bubble.scale.copyFrom(this.bubble.scale);
		return bubble;
	}
}